import { FC, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import AddIcon from "@mui/icons-material/Add";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";

import Dialog from "../dialog";

// Tab indices on the Essentials Support page (see src/pages/Help/support/Support.tsx).
const SUPPORT_TAB_PAYMENT_ACCOUNT = 4;
const SUPPORT_TAB_SERVICE_CONTACT = 6;

interface PaymentAccountSetupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  // "filer" is shown to non-admin firm users, who can't configure payment
  // accounts themselves and must be directed to their firm administrator.
  variant?: "default" | "filer";
}

const PaymentAccountSetupDialog: FC<PaymentAccountSetupDialogProps> = ({
  isOpen,
  onClose,
  variant = "default",
}) => {
  const navigate = useNavigate();

  const goToPaymentAccounts = useCallback(() => {
    onClose();
    navigate("/paymentAccounts");
  }, [navigate, onClose]);

  // Passes the target tab via router state so the URL stays clean
  // (/support) — the tab index never appears in the address bar.
  const goToSupport = useCallback(
    (activeTab: number) => () => {
      onClose();
      navigate("/support", { state: { activeTab } });
    },
    [navigate, onClose]
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title=""
      maxWidth="sm"
      floatingClose
      disableBackdropClose
      className="payment-setup-popup"
    >
      <Box className="payment-setup-dialog text-center">
        <Box className="payment-setup-dialog-alert">
          <PriorityHighRoundedIcon className="payment-setup-dialog-alert-icon" />
          <Typography className="payment-setup-dialog-alert-text">
            Setup incomplete — finish the items below to unlock e-filing.
          </Typography>
        </Box>

        <Box className="payment-setup-dialog-icon-wrap">
          <AccountBalanceWalletOutlinedIcon className="payment-setup-dialog-icon" />
        </Box>

        <Typography className="payment-setup-dialog-heading">
          Almost ready — just a quick setup to go
        </Typography>
        <Typography className="payment-setup-dialog-subtitle">
          {variant === "filer"
            ? "Your firm administrator has to finish the items below before the firm can e-file."
            : "Link your payment account once — then every filing is just a few clicks away."}
        </Typography>

        <Box className="payment-setup-dialog-steps">
          <Box className="payment-setup-dialog-step">
            <Box className="payment-setup-dialog-step-icon">
              <PaymentsOutlinedIcon />
            </Box>
            <Typography className="payment-setup-dialog-step-text">
              Filings can't be submitted without a payment account. Learn how to
              add one{" "}
              <Link
                component="button"
                type="button"
                className="payment-setup-dialog-step-link"
                onClick={goToSupport(SUPPORT_TAB_PAYMENT_ACCOUNT)}
              >
                here
              </Link>
              .
            </Typography>
          </Box>

          <Box className="payment-setup-dialog-step">
            <Box className="payment-setup-dialog-step-icon">
              <ContactPhoneOutlinedIcon />
            </Box>
            <Typography className="payment-setup-dialog-step-text">
              Adding a service contact keeps court messages flowing to the
              right inbox — see how to add{" "}
              <Link
                component="button"
                type="button"
                className="payment-setup-dialog-step-link"
                onClick={goToSupport(SUPPORT_TAB_SERVICE_CONTACT)}
              >
                here
              </Link>
              .
            </Typography>
          </Box>

          {variant === "filer" && (
            <Box className="payment-setup-dialog-step payment-setup-dialog-step-highlight">
              <Box className="payment-setup-dialog-step-icon payment-setup-dialog-step-icon-highlight">
                <SupervisorAccountOutlinedIcon />
              </Box>
              <Typography className="payment-setup-dialog-step-text">
                Reach out to your firm administrator to finish setting up the account
              </Typography>
            </Box>
          )}
        </Box>

        {variant !== "filer" && (
          <>
            <Button
              type="button"
              variant="contained"
              className="btn payment-setup-dialog-cta"
              startIcon={<AddIcon />}
              aria-label="Add a payment account"
              onClick={goToPaymentAccounts}
            >
              Add a payment account
            </Button>
            <Typography className="payment-setup-dialog-note">
              It only takes a minute to add payment account.
            </Typography>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default PaymentAccountSetupDialog;
